import React, { useMemo, useState, useCallback } from "react";
import {
  X,
  ArrowLeft,
  Link as LinkIcon,
  ExternalLink,
  Maximize2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Tooltip } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useEntity } from "./RMS/Data/entity";
import { useFlash } from "@/hooks/productTimerHooks";
import type {
  EntityKind,
  EntitySchema,
  ScalarFact,
  ArrayFact,
  BackreferenceGroup,
  Fact,
} from "./RMS/Dashboard/types";
import { isArrayFact, isScalarFact } from "./RMS/Dashboard/types";
import { storedFacts } from "./RMS/Dashboard/Overview/previewData";
import EntityIcon from "./RMS/Dashboard/Components/EntityIcon";
import { entityKindMap } from "./RMS/Dashboard/utils";
import FactRenderer from "./RMS/Dashboard/Components/FactRenderer";
import InlineEntityCard from "./RMS/Dashboard/Cards/InlineEntityCard";
import { createEntitySlug } from "./RMS/Dashboard/utils/slugUtils";
import { EntityPreviewContext } from "./RmsEntityPreviewContext";
import { currentViewportTier } from "@/hooks/useViewportTier";
import ViewerModeToggle, { useViewerModeAction } from "./ViewerModeToggle";
import {
  artifactToolbarButtonClass,
  paneToolbarDisabledClass,
} from "../FileViewer/paneToolbar";

// A related group longer than this shows its first rows and a "Show all":
// a firm with dozens of vehicles otherwise pushes the rest of the panel out
// of view.
const RELATED_ROWS_SHOWN = 5;

interface RmsEntityPreviewProps {
  entityKind: EntityKind;
  entityId: string;
  factNames?: string[];
  onClose: () => void;
  onBack?: () => void;
}

const factHasValue = (fact: ScalarFact | ArrayFact): boolean => {
  if (isArrayFact(fact)) {
    return fact.array.length > 0;
  }
  return fact.scalar !== null || fact.entity !== null;
};

const entityKindLabels: Record<string, string> = {
  person: "People",
  company: "Companies",
  manager: "Managers",
  vehicle: "Vehicles",
  topic: "Topics",
  interaction: "Interactions",
  client: "Clients",
  portfolio: "Portfolios",
  deal: "Deals",
};

const entityKindOrder: EntityKind[] = [
  "manager",
  "vehicle",
  "company",
  "person",
  "topic",
  "interaction",
  "client",
  "portfolio",
  "deal",
];

interface RelationshipEntry {
  entity: NonNullable<EntitySchema>;
  relationshipName: string;
}

const RmsEntityPreview: React.FC<RmsEntityPreviewProps> = ({
  entityKind,
  entityId,
  factNames,
  onClose,
  onBack,
}) => {
  const { data: entity, isLoading, error } = useEntity(entityKind, entityId);
  const location = useLocation();
  const navigate = useNavigate();
  // On the RMS view itself, the preview is a peek inside the same workspace,
  // so promoting it to the full page is a within-app navigation. Everywhere
  // else (chat, reports, matrices) the preview is a side-trip from unrelated
  // work, so open-full-page stays a new-tab so the original context is kept.
  const navigatesInPlace = location.pathname.startsWith("/rms/view/");
  const firstHighlightRef = React.useRef<HTMLDivElement>(null);
  const [copied, triggerCopied] = useFlash(2000);
  // Null where the layout toggle stands down: its hairline goes with it.
  const layoutAction = useViewerModeAction(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<EntityKind>>(
    new Set()
  );
  const [expandedGroups, setExpandedGroups] = useState<Set<EntityKind>>(
    new Set()
  );
  const [selectedFacts, setSelectedFacts] = useState<Set<string>>(
    new Set(factNames || [])
  );

  // Sync from props when they change (e.g. navigating to a different entity)
  React.useEffect(() => {
    setSelectedFacts(new Set(factNames || []));
  }, [entityKind, entityId, factNames]);

  // Another record's groups start open and cut again.
  React.useEffect(() => {
    setCollapsedGroups(new Set());
    setExpandedGroups(new Set());
  }, [entityKind, entityId]);

  const toggleExpanded = useCallback((kind: EntityKind) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) {
        next.delete(kind);
      } else {
        next.add(kind);
      }
      return next;
    });
  }, []);

  const toggleGroup = useCallback((kind: EntityKind) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) {
        next.delete(kind);
      } else {
        next.add(kind);
      }
      return next;
    });
  }, []);

  const updateFactNameUrlParams = useCallback((facts: Set<string>) => {
    // Phone (#11742): the preview is an ephemeral full-screen overlay
    // there — Layout skips its URL params the same way, and a write from
    // here would strand ?factName= after close. When it does write, it
    // carries history.state forward: a bare `{}` wiped the overlay's
    // back-dismissal marker (the entry then leaked a dead Back press) and
    // react-router's idx bookkeeping (review round 3).
    if (currentViewportTier() === "phone") return;
    const url = new URL(window.location.href);
    url.searchParams.delete("factName");
    for (const name of facts) {
      url.searchParams.append("factName", name);
    }
    window.history.replaceState(window.history.state ?? {}, "", url.toString());
  }, []);

  const handleFactClick = useCallback(
    (factName: string) => {
      setSelectedFacts((prev) => {
        const next = new Set(prev);
        if (next.has(factName)) {
          next.delete(factName);
        } else {
          next.add(factName);
        }
        updateFactNameUrlParams(next);
        return next;
      });
    },
    [updateFactNameUrlParams]
  );

  // Scroll first highlighted fact into view when selection changes
  React.useEffect(() => {
    if (entity && selectedFacts.size > 0) {
      // Defer to next frame so the ref is assigned after render
      requestAnimationFrame(() => {
        firstHighlightRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    }
  }, [entity, selectedFacts]);

  const getShareUrl = () => {
    // Build a URL with the current page path + entity preview params
    const url = new URL(window.location.href);
    url.searchParams.delete("factName");
    url.searchParams.set("entityId", entityId);
    for (const name of selectedFacts) {
      url.searchParams.append("factName", name);
    }
    return url.toString();
  };

  const getEntityFullPagePath = () => {
    if (!entity) return "";
    const slug = createEntitySlug({ name: entity.name, id: entity.id });
    return `/rms/${entity.kind}/${slug}`;
  };

  const getEntityFullPageUrl = () => {
    const path = getEntityFullPagePath();
    return path ? `${window.location.origin}${path}` : "";
  };

  const handleCopyLink = () => {
    const url = getShareUrl();
    navigator.clipboard.writeText(url);
    triggerCopied();
  };

  const handleOpenFullPage = () => {
    if (navigatesInPlace) {
      const path = getEntityFullPagePath();
      if (path) navigate(path);
      return;
    }
    const url = getEntityFullPageUrl();
    if (url) {
      window.open(url, "_blank");
    }
  };

  const handleEntityClick = (linkedEntity: {
    id: string;
    kind: string;
    name: string;
  }) => {
    window.dispatchEvent(
      new CustomEvent("openEntityPreview", {
        detail: {
          entityKind: linkedEntity.kind,
          entityId: linkedEntity.id,
        },
      })
    );
  };

  // Group relationships by entity kind (matching EntityRelationshipsPanel)
  const relationshipGroups = useMemo(() => {
    if (!entity) return [];

    const entityMap = new Map<EntityKind, RelationshipEntry[]>();

    // Forward facts with entity references
    entity.facts.forEach((fact: Fact) => {
      if (isScalarFact(fact) && fact.entity) {
        const kind = fact.entity.kind;
        if (!entityMap.has(kind)) entityMap.set(kind, []);
        const group = entityMap.get(kind)!;
        if (!group.some((e) => e.entity.id === fact.entity!.id)) {
          group.push({
            entity: fact.entity as NonNullable<EntitySchema>,
            relationshipName: fact.name,
          });
        }
      } else if (isArrayFact(fact)) {
        fact.array.forEach((factValue) => {
          if (factValue.entity) {
            const kind = factValue.entity.kind;
            if (!entityMap.has(kind)) entityMap.set(kind, []);
            const group = entityMap.get(kind)!;
            if (!group.some((e) => e.entity.id === factValue.entity!.id)) {
              group.push({
                entity: factValue.entity as NonNullable<EntitySchema>,
                relationshipName: fact.name,
              });
            }
          }
        });
      }
    });

    // Backreferences
    (entity.backreferences || []).forEach((backref: BackreferenceGroup) => {
      backref.entities.forEach((backrefEntity) => {
        if (backrefEntity.entity) {
          const kind = backrefEntity.entity.kind;
          if (!entityMap.has(kind)) entityMap.set(kind, []);
          const group = entityMap.get(kind)!;
          if (!group.some((e) => e.entity.id === backrefEntity.entity!.id)) {
            const name =
              backref.name && backref.name !== "INVERSE RELNAME UNSET"
                ? backref.name
                : backref.forward_name;
            group.push({
              entity: backrefEntity.entity as NonNullable<EntitySchema>,
              relationshipName: name,
            });
          }
        }
      });
    });

    return Array.from(entityMap.entries())
      .sort(
        (a, b) => entityKindOrder.indexOf(a[0]) - entityKindOrder.indexOf(b[0])
      )
      .map(([kind, entities]) => ({ kind, entities }));
  }, [entity]);

  // The artifact panes' header, drawn by hand: ArtifactPaneHeader always
  // carries a title menu, which this panel does not have (Gerry: its own
  // actions only). Keep the two in step: one 48px row with a hairline under
  // it; the record's kind and name lead; the panel's actions follow, the
  // layout toggle and Close after a hairline, as there.
  // A deep link opens the preview with the wildcard kind "entity", which no
  // icon draws: until the record loads, the kind is unknown and not shown.
  const kind: EntityKind | null =
    entity?.kind ?? (entityKind in entityKindMap ? entityKind : null);
  const kindLabel = kind ? entityKindMap[kind].label : null;
  const header = (
    <div className="flex h-12 flex-shrink-0 items-center gap-2 border-b border-[var(--fp-border)] pl-4 pr-3">
      {onBack && (
        <Tooltip title="Back" arrow placement="bottom">
          <button
            type="button"
            aria-label="Back"
            onClick={onBack}
            className={`-ml-1.5 ${artifactToolbarButtonClass}`}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        </Tooltip>
      )}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span
          aria-hidden="true"
          className="flex h-5 w-5 flex-none items-center justify-center rounded-[5px] bg-[#ECEDEF] text-[var(--fp-ink-2)]"
        >
          {kind && <EntityIcon kind={kind} size={13} />}
        </span>
        {kindLabel && (
          <span className="flex-none text-[10.5px] font-semibold uppercase tracking-[0.085em] text-[var(--fp-ink-3)]">
            {kindLabel}
          </span>
        )}
        {entity ? (
          <h2 className="min-w-0 truncate text-[14px] font-semibold tracking-[-0.011em] text-[var(--fp-ink)]">
            {entity.name}
          </h2>
        ) : (
          isLoading && (
            <span className="h-3.5 w-32 animate-pulse rounded bg-[var(--fp-bg-well)]" />
          )
        )}
      </div>
      <div className="flex flex-none items-center gap-1">
        <Tooltip
          title={copied ? "Copied!" : "Copy link"}
          arrow
          placement="bottom"
        >
          <button
            type="button"
            aria-label="Copy link"
            onClick={handleCopyLink}
            className={artifactToolbarButtonClass}
          >
            <LinkIcon className="h-4 w-4" />
          </button>
        </Tooltip>
        <Tooltip
          title={
            navigatesInPlace ? "Open full page" : "Open full page in new tab"
          }
          arrow
          placement="bottom"
        >
          <button
            type="button"
            aria-label={
              navigatesInPlace ? "Open full page" : "Open full page in new tab"
            }
            onClick={handleOpenFullPage}
            disabled={!entity}
            className={`${artifactToolbarButtonClass} ${paneToolbarDisabledClass}`}
          >
            {navigatesInPlace ? (
              <Maximize2 className="h-4 w-4" />
            ) : (
              <ExternalLink className="h-4 w-4" />
            )}
          </button>
        </Tooltip>
        {/* Overlay vs side-by-side, remembered per surface (#9746). Distinct
            from "Open full page", which navigates rather than re-lays out
            the pane. The toggle stands down at phone width and where only
            the overlay is offered, and its hairline with it. */}
        {layoutAction && (
          <>
            <span
              aria-hidden="true"
              className="mx-1 h-5 w-px flex-none bg-[var(--fp-border)]"
            />
            <ViewerModeToggle compact />
          </>
        )}
        <Tooltip title="Close" arrow placement="bottom">
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className={artifactToolbarButtonClass}
          >
            <X className="h-4 w-4" />
          </button>
        </Tooltip>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <div className="space-y-5 px-5 py-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="space-y-1.5">
              <div className="h-3 w-24 animate-pulse rounded bg-[var(--fp-bg-well)]" />
              <div className="h-3.5 w-48 animate-pulse rounded bg-[var(--fp-bg-well)]" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !entity) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <div className="flex flex-1 items-center justify-center">
          <p className="text-[13.5px] text-[var(--fp-ink-4)]">
            Couldn&apos;t load this record
          </p>
        </div>
      </div>
    );
  }

  // The record's own attributes only: a derived fact (Date Created, Created
  // By, Last Interaction, ...) shows where someone named it, and Date Created
  // is held on every record, so it would also keep "No facts yet" from ever
  // showing (#14680).
  const factsWithValues = storedFacts(entity).filter(factHasValue);

  return (
    <EntityPreviewContext.Provider value={true}>
      <div className="flex h-full flex-col">
        {header}

        {/* One surface: full-width hairlines between rows and no painted
            scrollbar, as every RMS inner region (rules/frontend.md,
            Scrollbars). */}
        <div className="fp-scroll-quiet min-h-0 flex-1 overflow-y-auto">
          {factsWithValues.length === 0 && relationshipGroups.length === 0 ? (
            <p className="px-5 py-4 text-[13.5px] text-[var(--fp-ink-4)]">
              No facts yet...
            </p>
          ) : (
            <>
              <dl>
                {factsWithValues.map((fact, index) => {
                  const isHighlighted = selectedFacts.has(fact.name);
                  const isFirstHighlighted =
                    isHighlighted &&
                    factsWithValues.findIndex((f) =>
                      selectedFacts.has(f.name)
                    ) === index;
                  return (
                    <div
                      key={fact.name}
                      ref={isFirstHighlighted ? firstHighlightRef : undefined}
                      className={`cursor-pointer border-b border-[var(--fp-border)] px-5 py-3 transition-colors ${
                        isHighlighted
                          ? "ring-2 ring-inset ring-[var(--fp-accent)]"
                          : "hover:bg-[var(--fp-hover)]"
                      }`}
                      onClick={() => handleFactClick(fact.name)}
                    >
                      <dt className="mb-1 text-[12.5px] text-[var(--fp-ink-3)]">
                        {fact.name}
                      </dt>
                      <dd className="text-[13.5px] text-[var(--fp-ink)]">
                        <FactRenderer
                          fact={fact}
                          onEntityClick={handleEntityClick}
                        />
                      </dd>
                    </div>
                  );
                })}
              </dl>

              {/* Relationships grouped by kind: each group collapses, and
                  one longer than RELATED_ROWS_SHOWN is cut with a Show all. */}
              {relationshipGroups.length > 0 && (
                <section className="pb-3">
                  <h3 className="px-5 pb-1.5 pt-5 text-[10.5px] font-semibold uppercase tracking-[0.085em] text-[var(--fp-ink-3)]">
                    Relationships
                  </h3>
                  {relationshipGroups.map((group) => {
                    const isCollapsed = collapsedGroups.has(group.kind);
                    const isExpanded = expandedGroups.has(group.kind);
                    const kindLabel =
                      entityKindLabels[group.kind] || group.kind;
                    // Check if any "relName (KindLabel)" key is highlighted
                    const relNames = [
                      ...new Set(group.entities.map((e) => e.relationshipName)),
                    ];
                    const matchingFactKeys = relNames
                      .map((r) => `${r} (${kindLabel})`)
                      .filter((k) => selectedFacts.has(k));
                    const isHighlighted = matchingFactKeys.length > 0;
                    const isCut = group.entities.length > RELATED_ROWS_SHOWN;
                    const shown =
                      isCut && !isExpanded
                        ? group.entities.slice(0, RELATED_ROWS_SHOWN)
                        : group.entities;
                    return (
                      <div
                        key={group.kind}
                        className={
                          isHighlighted
                            ? "ring-2 ring-inset ring-[var(--fp-accent)]"
                            : undefined
                        }
                      >
                        <div className="flex h-8 items-center gap-1.5 px-5">
                          <button
                            type="button"
                            aria-expanded={!isCollapsed}
                            aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${kindLabel}`}
                            onClick={() => toggleGroup(group.kind)}
                            className="-ml-1.5 flex h-6 w-6 flex-none cursor-pointer items-center justify-center rounded-[5px] text-[var(--fp-ink-3)] hover:bg-[var(--fp-hover)] hover:text-[var(--fp-ink)]"
                          >
                            {isCollapsed ? (
                              <ChevronRight className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronDown className="h-3.5 w-3.5" />
                            )}
                          </button>
                          <span
                            className="cursor-pointer select-none text-[13.5px] font-medium text-[var(--fp-ink)]"
                            onClick={() => {
                              // Toggle all matching fact keys for this group
                              const keys = relNames.map(
                                (r) => `${r} (${kindLabel})`
                              );
                              setSelectedFacts((prev) => {
                                const next = new Set(prev);
                                const allSelected = keys.every((k) =>
                                  next.has(k)
                                );
                                for (const k of keys) {
                                  if (allSelected) {
                                    next.delete(k);
                                  } else {
                                    next.add(k);
                                  }
                                }
                                updateFactNameUrlParams(next);
                                return next;
                              });
                            }}
                          >
                            {kindLabel}
                          </span>
                          <span className="text-[13.5px] text-[var(--fp-ink-4)]">
                            {group.entities.length}
                          </span>
                        </div>
                        {!isCollapsed && (
                          <>
                            <ul>
                              {shown.map((item) => (
                                <li
                                  key={item.entity.id}
                                  className="flex h-8 items-center gap-3 pl-11 pr-5 hover:bg-[var(--fp-hover)]"
                                >
                                  <span className="min-w-0 truncate text-[13.5px]">
                                    <InlineEntityCard
                                      entity={item.entity}
                                      onEntityClick={handleEntityClick}
                                      hideIcon
                                    />
                                  </span>
                                  <span className="ml-auto max-w-[45%] flex-none truncate text-[12.5px] text-[var(--fp-ink-4)]">
                                    {item.relationshipName}
                                  </span>
                                </li>
                              ))}
                            </ul>
                            {isCut && (
                              <button
                                type="button"
                                onClick={() => toggleExpanded(group.kind)}
                                className="flex h-8 cursor-pointer items-center pl-11 pr-5 text-[12.5px] font-medium text-[var(--fp-ink-2)] hover:text-[var(--fp-ink)]"
                              >
                                {isExpanded
                                  ? "Show fewer"
                                  : `Show all ${group.entities.length}`}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </EntityPreviewContext.Provider>
  );
};

export default RmsEntityPreview;
